import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CheckSquare } from 'lucide-react'

import useRouter from '../../../hooks/useRouter'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import ActionItemModal from './ActionItemModal'
import ActionItemsStats from './ActionItemsStats'
import ActionItemsTable from './ActionItemsTable'

interface Props {
  meetingId?: string
}

const ActionItemsPanel: React.FC<Props> = ({ meetingId }) => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const projectId = meData?.me?.currentProject?.id
  const { history } = useRouter()
  const { canEdit, canDelete } = useModuleActions('MEETINGS', projectId)

  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  // Stats are computed across all action items in the project, not the filtered subset.
  const { data: allData } = useQuery(Query.PROJECT_ACTION_ITEMS, {
    variables: { projectId },
    skip: !projectId,
  })

  const statusArg = statusFilter === 'all' ? undefined : [statusFilter]

  const { data, refetch } = useQuery(Query.PROJECT_ACTION_ITEMS, {
    variables: { projectId, status: statusArg },
    skip: !projectId,
  })

  const items = useMemo(() => {
    const all = data?.projectActionItems || []
    if (meetingId) return all.filter((i: any) => i.meeting?.id === meetingId)
    return all
  }, [data, meetingId])

  const assignees = useMemo(() => {
    const map = new Map<string, string>()
    items.forEach((a: any) => {
      if (a.assignedTo?.id) map.set(a.assignedTo.id, a.assignedTo.name)
    })
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [items])

  const hasUnassigned = useMemo(() => items.some((a: any) => !a.assignedTo), [items])

  const [assigneeFilter, setAssigneeFilter] = useState<string>('all')

  const filteredItems = useMemo(() => {
    if (assigneeFilter === 'all') return items
    if (assigneeFilter === 'unassigned') return items.filter((a: any) => !a.assignedTo)
    return items.filter((a: any) => a.assignedTo?.id === assigneeFilter)
  }, [items, assigneeFilter])

  const statsItems = useMemo(() => {
    const all = allData?.projectActionItems || []
    if (meetingId) return all.filter((i: any) => i.meeting?.id === meetingId)
    return all
  }, [allData, meetingId])

  const [deleteActionItem] = useMutation(Mutation.DELETE_ACTION_ITEM, {
    refetchQueries: [
      { query: Query.PROJECT_ACTION_ITEMS, variables: { projectId, status: statusArg } },
      { query: Query.PROJECT_ACTION_ITEMS, variables: { projectId } },
    ],
  })

  const handleDelete = async (id: string) => {
    try {
      await deleteActionItem({ variables: { id } })
      toastr.success(t('Common_success'), t('ActionItem_deleted'))
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const handleOpenMeeting = (mid: string) => {
    history.push(`/project/${projectId}/meetings/${mid}`)
  }

  return (
    <div className="space-y-3">
      <ActionItemsStats items={statsItems} />

      <div className="py-2 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="font-bold text-lg">{filteredItems.length}</span>
          <span className="text-muted-foreground text-sm">{t('Common_total')}</span>
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-[180px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('ActionItem_filter_all')}</SelectItem>
            <SelectItem value="OPEN">{t('ActionItem_status_open')}</SelectItem>
            <SelectItem value="IN_PROGRESS">{t('ActionItem_status_in_progress')}</SelectItem>
            <SelectItem value="RESOLVED">{t('ActionItem_status_resolved')}</SelectItem>
            <SelectItem value="CLOSED">{t('ActionItem_status_closed')}</SelectItem>
          </SelectContent>
        </Select>

        {(assignees.length > 0 || hasUnassigned) && (
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="h-8 w-[200px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('ActionItem_assignee_all')}</SelectItem>
              {hasUnassigned && (
                <SelectItem value="unassigned">{t('ActionItem_assignee_unassigned')}</SelectItem>
              )}
              {assignees.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <CheckSquare className="h-12 w-12 mb-3 opacity-40" />
          <p className="text-sm">{t('ActionItem_empty_hint')}</p>
        </div>
      ) : (
        <ActionItemsTable
          items={filteredItems}
          showMeetingColumn={!meetingId}
          onEdit={
            canEdit
              ? (id) => {
                  setEditingId(id)
                  setModalOpen(true)
                }
              : undefined
          }
          onDelete={canDelete ? handleDelete : undefined}
          onOpenMeeting={handleOpenMeeting}
        />
      )}

      {modalOpen && editingId && (
        <ActionItemModal
          isOpen={modalOpen}
          onClose={(saved) => {
            setModalOpen(false)
            setEditingId(null)
            if (saved) refetch()
          }}
          actionItemId={editingId}
          meetingId={null}
        />
      )}
    </div>
  )
}

export default ActionItemsPanel
